import type {
  DataAttributeDto,
  DataEntityDto,
  DataModelState,
  DataTypeDto,
  EntityCardDto,
  EntityRelationDto,
  FieldListDto,
  RequiredDocumentsState,
  Site,
} from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { api, apiDownload, errorMessage } from '../../lib/api';
import { dateOnly } from '../../lib/format';
import { childrenOf } from '../../lib/links';
import { navigate } from '../../lib/router';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Button, Card, ErrorText, SectionLoader, Select } from '../../ui';
import { EntityForm, EntityLinks, missingDocs } from './DataModel';
import { fieldLabel, useFieldLists } from './FieldInput';
import { EntityAddition } from './Additions';
import { Settlements } from './Settlements';
import { ValueHistory } from './ValueHistory';
import { PendingValues, usePendingValues } from './PendingValues';

type Doc = EntityCardDto['documenti'][number];
type Mail = NonNullable<EntityCardDto['posta']>[number];
type Program = EntityCardDto['programmi'][number];
type Tab = 'collegati' | 'programmi' | 'documenti' | 'posta' | 'citazioni' | 'simili';
/** Sezioni che sono solo fonti: si aprono dal loro numero. */
const ON_DEMAND: Tab[] = ['citazioni', 'simili'];

/** Colonne mostrate negli elenchi della scheda: le altre si vedono aprendo la voce. */
const COLUMNS = 5;
/** Righe per pagina negli elenchi della scheda (le righe dei programmi le pagina il server, ADR 0120). */
const PAGE = 10;

/** Un riquadro della scheda: titolo con il numero, poi il suo elenco. */
function Box({
  title,
  count,
  action,
  children,
}: {
  title: string;
  count?: number;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card className="p-5">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold">{title}</h3>
        {count !== undefined && (
          <span className="rounded-md bg-accent/10 px-1.5 py-0.5 text-[11px] font-bold text-accent">
            {count.toLocaleString('it-IT')}
          </span>
        )}
        <span className="flex-1" />
        {action}
      </div>
      <div className="overflow-x-auto">{children}</div>
    </Card>
  );
}

/** Pagine di un elenco: «11–20 di 34», precedenti e successive. Niente se sta in una pagina. */
function Pager({
  from,
  shown,
  total,
  onPage,
}: {
  from: number;
  shown: number;
  total: number;
  onPage: (from: number) => void;
}) {
  if (total <= PAGE) return null;
  return (
    <div className="sticky left-0 mt-2 flex items-center gap-2 text-xs text-ink/55">
      <span className="flex-1 tabular-nums">
        {from + 1}–{from + shown} di {total.toLocaleString('it-IT')}
      </span>
      <Button
        variant="ghost"
        disabled={from === 0}
        onClick={() => onPage(Math.max(0, from - PAGE))}
      >
        Precedenti
      </Button>
      <Button variant="ghost" disabled={from + PAGE >= total} onClick={() => onPage(from + PAGE)}>
        Successive
      </Button>
    </div>
  );
}

/** Un elenco già tutto nella scheda, mostrato una pagina alla volta. */
function Paged<T>({ items, children }: { items: T[]; children: (page: T[]) => ReactNode }) {
  const [from, setFrom] = useState(0);
  const start = from < items.length ? from : 0;
  const page = items.slice(start, start + PAGE);
  return (
    <>
      {children(page)}
      <Pager from={start} shown={page.length} total={items.length} onPage={setFrom} />
    </>
  );
}

/** Le fonti di un valore come numero: al clic si vede da dove viene. */
function SourceCount({ source }: { source: string | undefined }) {
  const [open, setOpen] = useState(false);
  if (!source) return null;
  return (
    <>
      <button
        type="button"
        className="ml-1.5 inline-grid h-4 min-w-4 place-items-center rounded-full bg-ink/[0.06] px-1 align-middle text-[10px] font-semibold text-ink/50 hover:bg-accent/15 hover:text-accent"
        title="Da dove viene"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        1
      </button>
      {open && (
        <span className="mt-1 block rounded-md bg-paper px-2 py-1 text-[11px] text-ink/60">
          da {source}
        </span>
      )}
    </>
  );
}

/** Un numero della scheda: quante cose ci sono; al clic si apre il loro elenco. */
function Tile({
  label,
  value,
  active,
  onClick,
}: {
  label: string;
  value: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!value}
      className={`rounded-xl px-4 py-3 text-left transition-colors disabled:cursor-default ${active ? 'bg-accent text-white shadow-[0_4px_14px_color-mix(in_srgb,var(--color-accent)_25%,transparent)]' : 'bg-white shadow-[0_0_20px_rgba(8,21,66,0.05)] enabled:hover:bg-accent/[0.04]'}`}
    >
      <span
        className={`block text-2xl font-semibold tabular-nums ${!value && !active ? 'text-ink/30' : ''}`}
      >
        {value.toLocaleString('it-IT')}
      </span>
      <span className={`block text-xs ${active ? 'text-white/80' : 'text-ink/55'}`}>{label}</span>
    </button>
  );
}

const cell = (v: unknown, a?: DataAttributeDto, lists?: Map<string, FieldListDto>) =>
  v === null || v === undefined || v === '' ? <span className="text-ink/30">—</span> : fieldLabel(a, v, lists);

/** Le voci di un altro tipo collegate a questa, come elenco del loro tipo: nome che apre la scheda e le prime colonne. */
function RelatedTable({
  type,
  items,
  typeKey,
  tenantId,
}: {
  type: DataTypeDto;
  items: Array<{ entity: DataEntityDto; via: string | null }>;
  typeKey: string;
  tenantId: string;
}) {
  // Le colonne che nominano questa stessa voce non dicono nulla di nuovo.
  const lists = useFieldLists(type.attributi);
  const cols = type.attributi
    .filter((a) => a.nome !== 'nome' && a.riferisce !== typeKey)
    .slice(0, COLUMNS - 1);
  const showVia = new Set(items.map((i) => i.via)).size > 1;
  return (
    <table className="bk-list min-w-max">
      <thead>
        <tr>
          <th>Nome</th>
          {cols.map((a) => (
            <th key={a.nome}>{a.etichetta}</th>
          ))}
          {showVia && <th>Legame</th>}
        </tr>
      </thead>
      <tbody>
        {items.map(({ entity, via }) => (
          <tr key={entity.rif}>
            <td className="font-medium">
              <a
                className="text-accent hover:underline"
                href={`#/t/${tenantId}/e/${type.chiave}/${encodeURIComponent(entity.rif)}`}
              >
                {entity.nome}
              </a>
            </td>
            {cols.map((a) => (
              <td key={a.nome} className="max-w-[18rem] truncate">
                {cell(entity.attributi[a.nome], a, lists)}
              </td>
            ))}
            {showVia && <td className="text-xs text-ink/55">{via ?? '—'}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Un valore di un programma: le date e i numeri all'italiana, il resto com'è. */
const programCell = (v: unknown) =>
  typeof v === 'number'
    ? v.toLocaleString('it-IT')
    : typeof v === 'string' && /^\d{4}-\d{2}-\d{2}(T|$)/.test(v)
      ? dateOnly(v)
      : cell(v);

/** Le righe di un programma collegato che la nominano (ADR 0120), con le prime colonne del programma. */
function ProgramTable({ program }: { program: Program }) {
  return (
    <table className="bk-list min-w-max">
      <thead>
        <tr>
          {program.colonne.map((col) => (
            <th key={col.nome}>{col.etichetta}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {program.righe.map((r) => (
          <tr key={r.rif}>
            {program.colonne.map((col) => (
              <td key={col.nome} className="max-w-[18rem] truncate">
                {programCell(r.valori[col.nome])}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Riquadro di un programma: la prima pagina arriva con la scheda, le altre si chiedono al server. */
function ProgramBox({
  program,
  typeKey,
  rif,
  tenantId,
}: {
  program: Program;
  typeKey: string;
  rif: string;
  tenantId: string;
}) {
  const [from, setFrom] = useState(0);
  const q = useTenantQuery<Program>(
    ['data-model', 'card', typeKey, rif, 'programma', program.tipo, from],
    `/data-model/types/${typeKey}/entities/${encodeURIComponent(rif)}/programs/${program.tipo}?offset=${from}`,
    { enabled: from > 0 },
  );
  const page = from === 0 ? program : q.data;
  return (
    <Box
      title={program.etichetta}
      count={program.totale}
      action={
        <>
          <span className="text-[11px] text-ink/45">da {program.programma}</span>
          <a
            className="text-xs font-medium text-accent hover:underline"
            href={`#/t/${tenantId}/dati/salvati/${program.tipo}`}
          >
            Tutti
          </a>
        </>
      }
    >
      <ErrorText>{q.error ? errorMessage(q.error) : ''}</ErrorText>
      {page ? <ProgramTable program={page} /> : <SectionLoader />}
      <Pager from={from} shown={page?.righe.length ?? 0} total={program.totale} onPage={setFrom} />
    </Box>
  );
}

/** Articoli all'inizio della descrizione: «una fattura ricevuta…» si raggruppa con «fattura emessa…». */
const ARTICLE = /^(?:(?:il|lo|la|i|gli|le|uno|una|un)\s+|l['’]\s*)/i;

/**
 * Il genere di un documento del rapporto, dalla prima parola di come il sistema l'ha descritto («fattura ricevuta per
 * servizi contabili» → Fattura): nessun elenco fisso, i gruppi vengono dalle descrizioni dell'azienda.
 */
function kindOf(d: Doc): string {
  if (!d.che_cosa) return d.perche === 'fonte' ? 'Da cui risultano i suoi dati' : 'Altri documenti';
  const word =
    d.che_cosa
      .trim()
      .replace(ARTICLE, '')
      .split(/[\s,.;:()]+/)[0] ?? '';
  return word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : 'Altri documenti';
}

function DocTable({ docs, onOpen }: { docs: Doc[]; onOpen: (d: Doc) => void }) {
  return (
    <table className="bk-list min-w-[40rem] table-fixed">
      <colgroup>
        <col className="w-32" />
        <col className="w-[38%]" />
        <col />
      </colgroup>
      <thead>
        <tr>
          <th>Data</th>
          <th>Documento</th>
          <th>Che cos’è</th>
        </tr>
      </thead>
      <tbody>
        {docs.map((d) => (
          <tr key={d.id}>
            <td className="whitespace-nowrap tabular-nums">
              {dateOnly(d.data_documento ?? d.data)}
            </td>
            <td className="max-w-[22rem] truncate">
              <button
                className="text-left text-accent hover:underline"
                title="Scarica il file"
                onClick={() => onOpen(d)}
              >
                {d.titolo}
              </button>
            </td>
            <td className="max-w-[28rem] truncate text-ink/70" title={d.che_cosa ?? undefined}>
              {d.che_cosa ??
                (d.perche === 'fonte'
                  ? 'da qui risultano i suoi dati'
                  : d.perche === 'collegato'
                    ? 'legato a lei'
                    : '—')}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function MailTable({ items }: { items: Mail[] }) {
  return (
    <table className="bk-list min-w-[44rem] table-fixed">
      <colgroup>
        <col className="w-32" />
        <col className="w-[34%]" />
        <col className="w-[20%]" />
        <col />
      </colgroup>
      <thead>
        <tr>
          <th>Data</th>
          <th>Oggetto</th>
          <th>Da</th>
          <th>Che cos’è</th>
        </tr>
      </thead>
      <tbody>
        {items.map((m) => (
          <tr key={m.rif}>
            <td className="whitespace-nowrap tabular-nums">{dateOnly(m.data)}</td>
            <td className="max-w-[22rem] truncate font-medium">{m.oggetto || '(senza oggetto)'}</td>
            <td className="max-w-[14rem] truncate text-ink/70">{m.da ?? '—'}</td>
            <td className="max-w-[24rem] truncate text-ink/70" title={m.passaggio ?? undefined}>
              {m.che_cosa ?? (m.passaggio ? `«${m.passaggio}»` : '—')}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Scheda di un'entità come la pagina di dettaglio di un gestionale (ADR 0119): in testa i suoi dati, con le fonti di
 * ogni valore come un numero; poi i numeri di ciò che la riguarda (collegati, documenti, posta, citazioni, simili) e,
 * sotto, gli elenchi: per ogni tipo collegato l'elenco filtrato su di lei, i documenti del rapporto divisi per genere.
 */
/**
 * La persona sposta la voce in un altro tipo (ADR 0168): valori, altri nomi, legami e documenti vanno con lei, e nel
 * tipo di arrivo si unisce allo stesso soggetto. Poi si apre la sua scheda nel tipo nuovo.
 */
function MoveEntity({ typeKey, entity, types }: { typeKey: string; entity: DataEntityDto; types: DataTypeDto[] }) {
  const { tenant, t } = useTenant();
  const queryClient = useQueryClient();
  const current = types.find((x) => x.chiave === typeKey);
  // Prima i tipi dello stesso genere (soggetti con soggetti, cose con cose).
  const options = types
    .filter((x) => x.stato === 'attivo' && x.chiave !== typeKey)
    .sort((a, b) => Number(b.soggetto_esterno === current?.soggetto_esterno) - Number(a.soggetto_esterno === current?.soggetto_esterno) || a.etichetta.localeCompare(b.etichetta, 'it'));
  const move = useMutation({
    mutationFn: (to: string) => api<DataModelState>(t(`/data-model/types/${typeKey}/entities/${encodeURIComponent(entity.rif)}/move`), { body: { in: to } }),
    onSuccess: (s, to) => {
      queryClient.setQueryData(['tenant', tenant.id, 'data-model'], s);
      void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'data-model', 'card'] });
      const arrived = s.data.find((x) => x.chiave === to)?.entita.find((x) => x.nome === entity.nome || x.altri_nomi.includes(entity.nome));
      navigate(arrived ? `/t/${tenant.id}/e/${to}/${encodeURIComponent(arrived.rif)}` : `/t/${tenant.id}/e/${to}`);
    },
  });
  if (!options.length) return null;
  return (
    <span className="inline-flex items-center gap-1 text-xs">
      <Select
        value=""
        disabled={move.isPending}
        aria-label="Sposta in un altro tipo"
        onChange={(ev) => {
          const to = ev.target.value;
          const label = options.find((x) => x.chiave === to)?.etichetta;
          if (to && window.confirm(`Spostare «${entity.nome}» in «${label}»? Valori, documenti e legami vanno con lei.`)) move.mutate(to);
        }}
      >
        <option value="">{move.isPending ? 'Sposto…' : 'Sposta in un altro tipo…'}</option>
        {options.map((x) => (
          <option key={x.chiave} value={x.chiave}>
            {x.etichetta}
          </option>
        ))}
      </Select>
      {move.error && <ErrorText>{errorMessage(move.error)}</ErrorText>}
    </span>
  );
}

const num = (v: number | null, digits = 2) =>
  v == null ? '' : v.toLocaleString('it-IT', { minimumFractionDigits: digits, maximumFractionDigits: Math.max(digits, 6) });
const rate = (aliquota: number | null, natura: string | null) => (natura ? natura : aliquota == null ? '' : `${num(aliquota, 0)}%`);

/** Righe e riepiloghi IVA della fattura elettronica, come stanno nel suo file (ADR 0216 § 7). */
function InvoiceLines({ invoice }: { invoice: NonNullable<EntityCardDto['fattura_elettronica']>[number] }) {
  const cur = invoice.divisa && invoice.divisa !== 'EUR' ? ` ${invoice.divisa}` : ' €';
  return (
    <Card className="overflow-hidden p-0">
      <div className="border-b border-black/5 px-6 py-3">
        <h2 className="text-sm font-semibold">Righe della fattura</h2>
        <p className="text-xs text-ink/55">Dal file {invoice.titolo}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wide text-ink/45">
            <tr>
              <th className="px-6 py-2 font-medium">Descrizione</th>
              <th className="px-3 py-2 text-right font-medium">Quantità</th>
              <th className="px-3 py-2 text-right font-medium">Prezzo</th>
              <th className="px-3 py-2 text-right font-medium">IVA</th>
              <th className="px-6 py-2 text-right font-medium">Importo</th>
            </tr>
          </thead>
          <tbody>
            {invoice.righe.map((r, i) => (
              <tr key={i} className="border-t border-black/5 align-top">
                <td className="px-6 py-2">{r.descrizione}</td>
                <td className="whitespace-nowrap px-3 py-2 text-right">
                  {r.quantita == null ? '' : `${num(r.quantita, 0)}${r.unita_misura ? ` ${r.unita_misura}` : ''}`}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right">{r.prezzo_unitario == null ? '' : `${num(r.prezzo_unitario)}${cur}`}</td>
                <td className="whitespace-nowrap px-3 py-2 text-right">{rate(r.aliquota_iva, r.natura)}</td>
                <td className="whitespace-nowrap px-6 py-2 text-right">{r.prezzo_totale == null ? '' : `${num(r.prezzo_totale)}${cur}`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!!invoice.riepiloghi.length && (
        <div className="border-t border-black/5 bg-black/[0.02] px-6 py-3">
          <h3 className="text-[11px] font-medium uppercase tracking-wide text-ink/45">Riepilogo IVA</h3>
          <ul className="mt-1 space-y-0.5 text-sm">
            {invoice.riepiloghi.map((r, i) => (
              <li key={i} className="flex flex-wrap gap-x-4">
                <span className="w-20">{rate(r.aliquota_iva, r.natura)}</span>
                <span>Imponibile {num(r.imponibile)}{cur}</span>
                <span>Imposta {num(r.imposta)}{cur}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

export function EntityCardPage({ typeKey, rif }: { typeKey: string; rif: string }) {
  const { tenant, isManager } = useTenant();
  const queryClient = useQueryClient();
  const path = `/data-model/types/${typeKey}/entities/${encodeURIComponent(rif)}`;
  const q = useTenantQuery<EntityCardDto>(['data-model', 'card', typeKey, rif], path);
  const model = useTenantQuery<DataModelState>(['data-model'], '/data-model');
  const required = useTenantQuery<RequiredDocumentsState>(
    ['archive', 'required'],
    '/archive/required',
  );
  const sitesQ = useTenantQuery<{ data: Site[] }>(['sites'], '/sites');
  const sites = (sitesQ.data?.data ?? []).filter((x) => x.status !== 'chiusa');
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState<Tab | null>(null);
  const [downloadError, setDownloadError] = useState('');
  // Le voci delle tendine, per mostrare l'etichetta al posto della chiave (ADR 0208).
  const lists = useFieldLists(q.data?.tipo.attributi ?? []);
  const pendingQ = usePendingValues(typeKey, rif);
  // Si torna all'elenco del tipo (ADR 0112).
  const back = () => navigate(`/t/${tenant.id}/e/${typeKey}`);
  if (q.isLoading) return <SectionLoader />;
  if (q.error || !q.data)
    return (
      <Card>
        <ErrorText>{q.error ? errorMessage(q.error) : 'Scheda non disponibile'}</ErrorText>
        <Button variant="ghost" className="mt-2" onClick={back}>
          Torna all’elenco
        </Button>
      </Card>
    );
  const c = q.data;
  const e = c.entita;
  const types = model.data?.data ?? [];
  const type = types.find((x) => x.chiave === typeKey);
  const missing = missingDocs(required.data, e.rif);
  const download = (d: { id: string; titolo: string }) =>
    apiDownload(`/tenants/${tenant.id}/archive/documents/${d.id}/file`, d.titolo).catch(
      (err: unknown) => setDownloadError(`${d.titolo}: ${errorMessage(err)}`),
    );
  const saved = (s: DataModelState) => {
    setEditing(false);
    queryClient.setQueryData(['tenant', tenant.id, 'data-model'], s);
    void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'data-model', 'card'] });
    // Togliere l'entità riporta all'elenco.
    if (!s.data.find((x) => x.chiave === typeKey)?.entita.some((x) => x.rif === e.rif)) back();
  };

  // Collegati: per ogni tipo del modello le sue voci legate a questa; persone e nomi non riconosciuti a parte.
  const byType = new Map<string, Array<{ entity: DataEntityDto; via: string | null }>>();
  const loose: EntityRelationDto[] = [];
  for (const r of c.relazioni) {
    const other = types.find((x) => x.chiave === r.tipo);
    const entity = r.rif ? other?.entita.find((x) => x.rif === r.rif) : undefined;
    if (!other || !entity) {
      loose.push(r);
      continue;
    }
    const via =
      r.verso === 'stesso_soggetto'
        ? 'stesso soggetto'
        : r.attributo
          ? `${r.verso === 'uscente' ? '' : 'la nomina come '}${r.attributo.toLowerCase()}`
          : null;
    const list = byType.get(r.tipo) ?? [];
    if (!list.some((x) => x.entity.rif === entity.rif)) list.push({ entity, via });
    byType.set(r.tipo, list);
  }
  // Le voci figlie (ADR 0238): i movimenti nella scheda del conto, prima degli altri collegati.
  const children = childrenOf(types, typeKey, e);
  for (const ch of children) {
    const list = byType.get(ch.type.chiave) ?? [];
    for (const x of ch.items) if (!list.some((y) => y.entity.rif === x.rif)) list.push({ entity: x, via: ch.link.significato });
    byType.set(ch.type.chiave, list);
  }
  const childOf = new Map(children.map((ch) => [ch.type.chiave, ch.link]));
  // Anche senza figlie la scheda dice che cosa può contenere, per aggiungerne una (il conto senza movimenti).
  for (const t of types)
    for (const l of t.legami_tipo ?? [])
      if (isManager && l.obbligo === 'genitore' && l.tipo === typeKey && t.stato === 'attivo' && !childOf.has(t.chiave)) {
        childOf.set(t.chiave, l);
        if (!byType.has(t.chiave)) byType.set(t.chiave, []);
      }
  const groups = [...byType].sort((a, b) => Number(childOf.has(b[0])) - Number(childOf.has(a[0])) || b[1].length - a[1].length);
  const related = groups.reduce((n, [, l]) => n + l.length, 0) + loose.length;

  // Documenti del rapporto divisi per genere, i più numerosi prima.
  const kinds = new Map<string, Doc[]>();
  for (const d of c.documenti) kinds.set(kindOf(d), [...(kinds.get(kindOf(d)) ?? []), d]);
  const docGroups = [...kinds].sort((a, b) => b[1].length - a[1].length);
  const mail = c.posta ?? [];
  const linkedMail = mail.filter((m) => m.legato);
  const otherMail = mail.filter((m) => !m.legato);

  // Un'API di prima (ADR 0120) non manda le righe dei programmi.
  const programs = c.programmi ?? [];
  const programRows = programs.reduce((n, p) => n + p.totale, 0);

  const tiles: Array<{ key: Tab; label: string; value: number }> = [
    { key: 'collegati', label: 'Collegati', value: related },
    ...(programs.length
      ? [{ key: 'programmi' as const, label: 'Dai programmi', value: programRows }]
      : []),
    { key: 'documenti', label: 'Documenti', value: c.documenti.length },
    ...(c.posta ? [{ key: 'posta' as const, label: 'Messaggi di posta', value: mail.length }] : []),
    { key: 'citazioni', label: 'La nominano', value: c.citazioni.length },
    { key: 'simili', label: 'Simili', value: c.vicini.length },
  ];
  // Un numero porta alla sua sezione; le sezioni che sono solo fonti si aprono (o richiudono) da qui.
  const show = (key: Tab) => {
    const next = ON_DEMAND.includes(key) && open === key ? null : key;
    setOpen(next);
    if (next)
      requestAnimationFrame(() =>
        document
          .getElementById(`sezione-${key}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      );
  };
  const values = c.tipo.attributi.filter((a) => a.nome !== 'nome');
  // I campi con un valore sospeso aperto (ADR 0211 passo 3): segnati anche se vuoti.
  const pendingAttrs = new Set((pendingQ.data?.sospesi ?? []).map((p) => p.attributo));
  const valueOf = (a: DataAttributeDto) => {
    const v = e.attributi[a.nome];
    if (v === null || v === undefined || v === '') return <span className="text-amber-700">—</span>;
    // Il valore che nomina un'altra cosa porta alla sua scheda.
    const other =
      a.riferisce && a.riferisce !== 'persona'
        ? types.find((x) => x.chiave === a.riferisce)
        : undefined;
    const hit = other?.entita.find((x) => x.nome.toLowerCase() === String(v).toLowerCase());
    return hit ? (
      <a
        className="text-accent hover:underline"
        href={`#/t/${tenant.id}/e/${other!.chiave}/${encodeURIComponent(hit.rif)}`}
      >
        {String(v)}
      </a>
    ) : (
      fieldLabel(a, v, lists)
    );
  };

  return (
    <div className="space-y-5">
      {/* Testata: chi è, i suoi dati e dove opera. */}
      <Card className="p-0">
        <div className="flex flex-wrap items-start gap-3 border-b border-black/5 px-6 py-5">
          <div className="min-w-0 flex-1">
            <button className="text-xs font-medium text-accent hover:underline" onClick={back}>
              ← {c.tipo.etichetta}
            </button>
            <h1 className="mt-1 text-xl font-semibold">
              {e.nome}
              <SourceCount source={e.fonti.nome} />
            </h1>
            {!!e.altri_nomi.length && (
              <p className="mt-0.5 text-xs text-ink/50">
                Trovata anche come {e.altri_nomi.join(' · ')}
              </p>
            )}
          </div>
          {isManager && type && !editing && (
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <MoveEntity typeKey={typeKey} entity={e} types={types} />
              <Button onClick={() => setEditing(true)}>Modifica</Button>
            </div>
          )}
        </div>
        {editing && type ? (
          <div className="p-5">
            <EntityForm
              type={type}
              entity={e}
              types={types}
              sites={sites}
              onSaved={saved}
              onCancel={() => setEditing(false)}
            />
          </div>
        ) : (
          <dl className="grid gap-x-8 gap-y-4 px-6 py-5 text-sm sm:grid-cols-2 xl:grid-cols-4">
            {values.map((a) => (
              <div key={a.nome} className="min-w-0">
                <dt className="text-[11px] font-medium uppercase tracking-wide text-ink/45">
                  {a.etichetta}
                </dt>
                <dd className="mt-0.5 break-words">
                  {valueOf(a)}
                  <SourceCount source={e.fonti[a.nome]} />
                  {pendingAttrs.has(a.nome) && <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800">da sistemare</span>}
                </dd>
              </div>
            ))}
            <div className="min-w-0">
              <dt className="text-[11px] font-medium uppercase tracking-wide text-ink/45">
                Sedi e attività
              </dt>
              <dd className="mt-0.5">
                <EntityLinks e={e} />
              </dd>
            </div>
          </dl>
        )}
        {!!missing.length && (
          <div className="flex flex-wrap items-center gap-2 border-t border-black/5 bg-amber-50/60 px-6 py-3 text-xs text-amber-800">
            <span className="font-semibold">
              {missing.length === 1
                ? 'Manca un documento obbligatorio:'
                : `Mancano ${missing.length} documenti obbligatori:`}
            </span>
            <span className="min-w-0 flex-1 truncate">{missing.join(', ')}</span>
            <a className="font-medium underline" href={`#/t/${tenant.id}/archivio`}>
              Carica
            </a>
          </div>
        )}
      </Card>

      {(c.fattura_elettronica ?? []).map((f) => (
        <InvoiceLines key={`${f.documento_id}:${f.numero ?? ''}`} invoice={f} />
      ))}

      {type && <EntityAddition type={type} entity={e} onDiscarded={back} />}
      {type && <PendingValues type={type} rif={e.rif} />}
      {type && <Settlements tipo={type.chiave} rif={e.rif} settleable={type.voce_strato === 's2.ent.fattura' || type.ruolo_documento === 'fattura_acquisto' || type.ruolo_documento === 'fattura_vendita'} />}
      {type && <ValueHistory type={type} rif={e.rif} />}

      {/* I numeri: al clic l'elenco che c'è dietro. */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-[repeat(auto-fit,minmax(9rem,1fr))]">
        {tiles.map((t) => (
          <Tile
            key={t.key}
            label={t.label}
            value={t.value}
            active={open === t.key && ON_DEMAND.includes(t.key)}
            onClick={() => show(t.key)}
          />
        ))}
      </div>
      <ErrorText>{downloadError}</ErrorText>

      {(related > 0 || byType.size > 0) && (
        <div id="sezione-collegati" className="scroll-mt-24 space-y-4">
          {groups.map(([key, items]) => {
            const t = types.find((x) => x.chiave === key)!;
            return (
              <Box
                key={key}
                title={t.etichetta}
                count={items.length}
                action={
                  <span className="flex gap-3">
                    {/* Una voce figlia nuova nasce già dentro questa (il movimento nel conto). */}
                    {childOf.has(key) && isManager && (
                      <a
                        className="text-xs font-medium text-accent hover:underline"
                        href={`#/t/${tenant.id}/nuova/${key}/${encodeURIComponent(childOf.get(key)!.significato)}/${encodeURIComponent(e.nome)}`}
                      >
                        Aggiungi
                      </a>
                    )}
                    <a
                      className="text-xs font-medium text-accent hover:underline"
                      href={`#/t/${tenant.id}/e/${key}`}
                    >
                      Tutti
                    </a>
                  </span>
                }
              >
                {items.length ? (
                  <Paged items={items}>
                    {(page) => (
                      <RelatedTable type={t} items={page} typeKey={typeKey} tenantId={tenant.id} />
                    )}
                  </Paged>
                ) : (
                  <p className="text-xs text-ink/50">Nessuna ancora.</p>
                )}
              </Box>
            );
          })}
          {!!loose.length && (
            <Box title="Altri nomi collegati" count={loose.length}>
              <ul className="flex flex-wrap gap-2 text-sm">
                {loose.map((r, i) => (
                  <li key={`${r.tipo}/${r.nome}/${i}`} className="rounded-lg bg-paper px-2.5 py-1">
                    {r.tipo === 'persona' && r.rif ? (
                      <a className="text-accent hover:underline" href={`#/t/${tenant.id}/persone`}>
                        {r.nome}
                      </a>
                    ) : (
                      <span title="Non corrisponde a nessuna voce nota">{r.nome}</span>
                    )}
                    <span className="ml-1 text-xs text-ink/45">
                      {[r.etichetta_tipo, r.attributo].filter(Boolean).join(' · ')}
                    </span>
                  </li>
                ))}
              </ul>
            </Box>
          )}
        </div>
      )}

      {programs.length > 0 && (
        <div id="sezione-programmi" className="scroll-mt-24 space-y-4">
          {programs.map((p) => (
            <ProgramBox
              key={p.tipo}
              program={p}
              typeKey={typeKey}
              rif={e.rif}
              tenantId={tenant.id}
            />
          ))}
        </div>
      )}

      {docGroups.length > 0 && (
        <div id="sezione-documenti" className="scroll-mt-24 space-y-4">
          {docGroups.map(([kind, docs]) => (
            <Box key={kind} title={kind} count={docs.length}>
              <Paged items={docs}>{(page) => <DocTable docs={page} onOpen={download} />}</Paged>
            </Box>
          ))}
        </div>
      )}

      {mail.length > 0 && (
        <div id="sezione-posta" className="scroll-mt-24 space-y-4">
          {!!linkedMail.length && (
            <Box title="Posta del rapporto" count={linkedMail.length}>
              <Paged items={linkedMail}>{(page) => <MailTable items={page} />}</Paged>
            </Box>
          )}
          {!!otherMail.length && (
            <Box title="Altri messaggi che la nominano" count={otherMail.length}>
              <Paged items={otherMail}>{(page) => <MailTable items={page} />}</Paged>
            </Box>
          )}
        </div>
      )}
      {!related && !programs.length && !docGroups.length && !mail.length && (
        <Empty>
          Nessun’altra cosa dei dati la nomina e nessun documento o messaggio è legato a questo
          rapporto.
        </Empty>
      )}

      {open === 'citazioni' && c.citazioni.length > 0 && (
        <div id="sezione-citazioni" className="scroll-mt-24">
          <Box title="Documenti che la nominano" count={c.citazioni.length}>
            <Paged items={c.citazioni}>
              {(page) => (
                <table className="bk-list min-w-max">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Documento</th>
                      <th>Dove la nomina</th>
                    </tr>
                  </thead>
                  <tbody>
                    {page.map((d) => (
                      <tr key={d.id}>
                        <td className="whitespace-nowrap tabular-nums">{dateOnly(d.data)}</td>
                        <td className="max-w-[22rem] truncate">
                          <button
                            className="text-left text-accent hover:underline"
                            onClick={() => download(d)}
                          >
                            {d.titolo}
                          </button>
                        </td>
                        <td
                          className="max-w-[32rem] truncate text-ink/65"
                          title={d.passaggio ?? undefined}
                        >
                          {d.passaggio ? `«${d.passaggio}»` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Paged>
            <p className="mt-3 text-[11px] text-ink/45">
              Cercata come {c.cercata_come.join(' · ') || '—'}: la contengono per parola, con nomi
              comuni possono esserci casi che non c’entrano.
            </p>
          </Box>
        </div>
      )}

      {open === 'simili' && c.vicini.length > 0 && (
        <div id="sezione-simili" className="scroll-mt-24">
          <Box title="Vicine per significato" count={c.vicini.length}>
            <Paged items={c.vicini}>
              {(page) => (
                <table className="bk-list min-w-max">
                  <thead>
                    <tr>
                      <th>Cosa</th>
                      <th>Di che tipo</th>
                      <th>Somiglianza</th>
                    </tr>
                  </thead>
                  <tbody>
                    {page.map((v) => (
                      <tr key={v.id}>
                        <td className="max-w-[26rem] truncate">
                          {v.fonte === 'documento' ? (
                            <button
                              className="text-left text-accent hover:underline"
                              onClick={() => download(v)}
                            >
                              {v.titolo}
                            </button>
                          ) : v.tipo && v.riferimento && v.tipo !== 'messaggio_posta' ? (
                            <a
                              className="text-accent hover:underline"
                              href={`#/t/${tenant.id}/e/${v.tipo}/${encodeURIComponent(v.riferimento)}`}
                            >
                              {v.titolo}
                            </a>
                          ) : (
                            v.titolo
                          )}
                        </td>
                        <td className="text-ink/65">
                          {v.fonte === 'documento' ? 'Documento' : (v.etichetta ?? '—')}
                        </td>
                        <td className="tabular-nums">{Math.round(v.somiglianza * 100)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Paged>
          </Box>
        </div>
      )}
    </div>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-xl bg-white px-5 py-8 text-center text-sm text-ink/50 shadow-[0_0_20px_rgba(8,21,66,0.05)]">
      {children}
    </p>
  );
}
